import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import ApexGlyph from './ApexGlyph';

const NAV_LINKS = [
  { label: 'Why Apex', to: '/why-apex' },
  { label: 'Platform', to: '/platform' },
  { label: 'Lens', to: '/product/lens' },
  { label: 'Trust', to: '/product/trust' },
  { label: 'How it works', to: '/lens/how-it-works' },
];

export default function LandingNav() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const navStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 60,
    transition: 'background 0.25s ease, border-color 0.25s ease, backdrop-filter 0.25s ease',
    background: scrolled ? 'rgba(6,7,10,0.85)' : 'transparent',
    backdropFilter: scrolled ? 'blur(12px)' : 'none',
    WebkitBackdropFilter: scrolled ? 'blur(12px)' : 'none',
    borderBottom: scrolled ? '1px solid #14171F' : '1px solid transparent',
  };

  return (
    <>
      <nav style={navStyle} data-testid="landing-nav">
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Left brand — simplified monochrome glyph + wordmark */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
            <ApexGlyph size={30} color="#E2E8F0" />
            <span style={{ color: '#E2E8F0', fontWeight: 500, fontSize: 14, letterSpacing: '0.22em', textTransform: 'uppercase' }}>APEX Verity</span>
          </Link>

          {/* Center desktop links */}
          <div className="hidden md:flex" style={{ gap: 28, alignItems: 'center' }}>
            {NAV_LINKS.map((l) => {
              const active = location.pathname === l.to;
              return (
                <Link key={l.to} to={l.to} style={{
                  fontSize: 13, textDecoration: 'none',
                  color: active ? '#F8FAFC' : '#64748B',
                  transition: 'color 0.2s',
                }} onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = '#94A3B8'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = '#64748B'; }}
                  data-testid={`nav-${l.to.replace(/\//g, '-')}`}>
                  {l.label}
                </Link>
              );
            })}
          </div>

          {/* Right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Link to="/contact" className="hidden md:inline-flex" style={{
              alignItems: 'center', gap: 6,
              background: '#0D9488', color: 'white', padding: '7px 16px',
              borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: 'none',
              transition: 'background 0.2s',
            }} onMouseEnter={(e) => e.currentTarget.style.background = '#0B7A70'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#0D9488'}
              data-testid="nav-request-pilot">
              Request pilot →
            </Link>
            <button className="md:hidden" onClick={() => setOpen(!open)}
              style={{ background: 'transparent', border: 'none', color: '#94A3B8', padding: 4, cursor: 'pointer' }}
              aria-label="Toggle menu" data-testid="nav-mobile-toggle">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {open && (
        <div className="md:hidden" style={{
          position: 'fixed', inset: 0, zIndex: 55, background: '#0A0A0F',
          padding: '72px 24px 32px', display: 'flex', flexDirection: 'column', gap: 18,
        }} data-testid="nav-mobile-overlay">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} style={{
              fontSize: 18, color: '#F8FAFC', textDecoration: 'none', fontWeight: 500,
              borderBottom: '1px solid #1E2A45', paddingBottom: 14,
            }}>{l.label}</Link>
          ))}
          <Link to="/contact" style={{
            marginTop: 12, background: '#0D9488', color: 'white',
            padding: '12px 18px', borderRadius: 10, fontSize: 14, fontWeight: 600,
            textAlign: 'center', textDecoration: 'none',
          }}>Request pilot →</Link>
        </div>
      )}
    </>
  );
}
